import React, {Component} from "react";
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {ClientHeader} from "./ClientHeader";
import {ClientTabs} from "./ClientTabs";
import {ActivityLogFilterBar} from "./activity_log/ActivityLogFilterBar";
import {ActivityLogEntryList} from "./activity_log/ActivityLogEntryList";
import {ActivityLogPagination} from "./activity_log/ActivityLogPagination";
import {ActivityLogCommentModal} from "./activity_log/ActivityLogCommentModal";
import {Navigate} from "react-router-dom";
import {isSuperAdmin} from "../../utils/FeatureFlags";
import {withTranslation} from 'react-i18next';
import {useFicha21} from "../../context/UserModulesContext";
import {Spinner} from "../Spinner";
import {fetchCustomMetrics} from "../../hooks/useCustomMetrics";
import {setCustomMetricLabelsCache} from "../dashboard/columns";
import {hasHistorial} from "./ia_booster/boosterSteps";
import {MemoryBitacoraView} from "./memory/MemoryBitacoraView";

class ClientActivityLogContentClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            entries: [],
            loading: true,
            totalCount: 0,
            totalPages: 1,
            currentPage: 1,
            pageSize: 25,
            filters: {
                category: '',
                bu_id: '',
                date_from: '',
                date_to: '',
                search: '',
            },
            businessUnits: [],
            isSubmittingComment: false,
            showCommentModal: false,
        };

        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handleCommentSubmit = this.handleCommentSubmit.bind(this);
        this.toggleCommentModal = this.toggleCommentModal.bind(this);
    }

    componentDidMount() {
        this.getClientData();
        this.fetchClassicEntriesIfNeeded();
        // Alertas de métricas custom: hidratar el cache de display_names para no mostrar el
        // raw "CUSTOM:6" en títulos y frases (mismo patrón que ControlCentralContent).
        fetchCustomMetrics().then((items) => {
            setCustomMetricLabelsCache(items);
            this.setState({customMetricLabelsReady: true});
        });
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.modulesLoaded && this.props.modulesLoaded) this.fetchClassicEntriesIfNeeded();
    }

    // El feed de acá es el de la pantalla CLÁSICA: en la 2.1 lo pide `MemoryBitacoraView`. Se
    // espera a saber cuál es (`modulesLoaded`), o en la 2.1 se pedía un feed que nadie mira.
    fetchClassicEntriesIfNeeded() {
        if (!this.props.modulesLoaded || this.props.ficha21 || this._classicFetched) return;
        this._classicFetched = true;
        this.fetchEntries();
    }

    getClientData() {
        new APIClient(API_URL + 'client/' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({name: data.data['name']});
                const bus = (data.data['business_units'] || []).map(bu => ({
                    id: bu.id, name: bu.name
                }));
                this.setState({businessUnits: bus});
            }
        });
    }

    fetchEntries() {
        this.setState({loading: true});
        const {filters, currentPage, pageSize} = this.state;
        let url = API_URL + 'client/' + this.props.clientId + '/activity-log/?page=' + currentPage + '&page_size=' + pageSize;

        if (filters.category) url += '&category=' + filters.category;
        if (filters.bu_id) url += '&bu_id=' + filters.bu_id;
        if (filters.date_from) url += '&date_from=' + filters.date_from + 'T00:00:00';
        if (filters.date_to) url += '&date_to=' + filters.date_to + 'T23:59:59';
        if (filters.search) url += '&search=' + encodeURIComponent(filters.search);

        new APIClient(url).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({
                    entries: data.data.entries,
                    totalCount: data.data.total_count,
                    totalPages: data.data.total_pages,
                    currentPage: data.data.current_page,
                    pageSize: data.data.page_size,
                    loading: false,
                });
            } else {
                this.setState({loading: false});
            }
        });
    }

    handleFilterChange(newFilters) {
        this.setState({filters: newFilters, currentPage: 1}, () => {
            this.fetchEntries();
        });
    }

    handlePageChange(page) {
        this.setState({currentPage: page}, () => {
            this.fetchEntries();
        });
    }

    toggleCommentModal() {
        this.setState({showCommentModal: !this.state.showCommentModal});
    }

    handleCommentSubmit(commentText, buId) {
        this.setState({isSubmittingComment: true});
        const body = {comment_text: commentText};
        if (buId) body.business_unit_id = buId;

        new APIClient(API_URL + 'client/' + this.props.clientId + '/activity-log/').call('POST', body).then((data) => {
            this.setState({isSubmittingComment: false});
            if (data && data.status === 'OK') {
                this.setState({showCommentModal: false});
                this.fetchEntries();
            } else if (data) {
                alert(data['error_message'] || 'Error');
            }
        });
    }

    renderHistorial() {
        // Ficha 2.1: esta ruta es la pestaña Historial del cliente (card 2BZI0Us8), a pantalla
        // completa y para todo usuario de la agencia —el endpoint ya es agency-only—. Hasta acá
        // redirigía a la bandeja del Recorrido, y desde el Panel no había forma de encontrarla.
        // Es la MISMA vista que esa bandeja (`MemoryBitacoraView`), con encabezado y export.
        if (!this.props.canSeeHistorial) {
            return <Navigate to={`/client/${this.props.clientId}/control-central`} replace/>;
        }
        return (
            <section className="section-content">
                <ClientHeader name={this.state.name}/>
                <ClientTabs clientId={this.props.clientId}/>
                <div className="section-body cmem-bitacora-section">
                    <div className="box cmem-bitacora-page">
                        <MemoryBitacoraView clientId={this.props.clientId} pageMode/>
                    </div>
                </div>
            </section>
        );
    }

    render() {
        const {t} = this.props;
        // `loaded` primero: hasta saber si es 2.1, un usuario que no es SuperAdmin rebotaría al
        // Monitoreo antes de ver su pestaña.
        if (!this.props.modulesLoaded) {
            return (
                <section className="section-content">
                    <ClientHeader name={this.state.name}/>
                    <ClientTabs clientId={this.props.clientId}/>
                    <div className="section-body">
                        <div className="box-loader"><Spinner color="positive"/></div>
                    </div>
                </section>
            );
        }
        if (this.props.ficha21) {
            return this.renderHistorial();
        }
        // Clásico: la Bitácora standalone sigue siendo sólo del equipo Dashbo (SuperAdmin).
        // Guard ante acceso directo por URL.
        if (!isSuperAdmin()) {
            return <Navigate to={`/client/${this.props.clientId}/monitoring`} replace/>;
        }
        return (
            <section className="section-content">
                <ClientHeader name={this.state.name}/>
                <ClientTabs clientId={this.props.clientId}/>

                <div className="section-body">
                    <div className="box">
                        <header className="box-header">
                            <h2 className="box-title">{t('activity_log.title')}</h2>
                            <button className="btn -alt" onClick={this.toggleCommentModal}>
                                {t('activity_log.add_comment')}
                            </button>
                        </header>

                        <ActivityLogFilterBar
                            filters={this.state.filters}
                            businessUnits={this.state.businessUnits}
                            onFilterChange={this.handleFilterChange}
                        />

                        <ActivityLogEntryList
                            entries={this.state.entries}
                            loading={this.state.loading}
                            clientId={this.props.clientId}
                        />

                        <ActivityLogPagination
                            currentPage={this.state.currentPage}
                            totalPages={this.state.totalPages}
                            totalCount={this.state.totalCount}
                            onPageChange={this.handlePageChange}
                        />
                    </div>
                </div>

                <ActivityLogCommentModal
                    isOpen={this.state.showCommentModal}
                    toggle={this.toggleCommentModal}
                    businessUnits={this.state.businessUnits}
                    onSubmit={this.handleCommentSubmit}
                    isSubmitting={this.state.isSubmittingComment}
                />
            </section>
        );
    }
}

const ClientActivityLogContentInner = withTranslation()(ClientActivityLogContentClass);

// Qué pantalla se dibuja sale del provider (fuente única). `loaded` evita pintar la pantalla
// vieja por un instante antes de saber si corresponde la pestaña Historial de la 2.1.
export function ClientActivityLogContent(props) {
    const {ficha21, loaded, modules, clientMemoryLevel} = useFicha21();
    // El mismo gate que la pestaña (`ClientTabs.hasHistorialTab`): una sola definición.
    const canSeeHistorial = hasHistorial({clientMemoryLevel, modules});
    return <ClientActivityLogContentInner {...props} ficha21={ficha21} modulesLoaded={loaded}
                                          canSeeHistorial={canSeeHistorial}/>;
}
